import { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Check } from 'lucide-react';
import { MotionReveal } from '@/components/common/MotionReveal';
import { BeforeAfterSlider } from './BeforeAfterSlider';

const processChecks = [
  'Jasno definisan rezultat',
  'Vlasnik svakog zadatka',
  'Dogovoreni rokovi',
  'Redovan pregled statusa'
];

const assessmentMessages = [
  'Tok rada još nije definisan.',
  'Početna tačka postoji, ali proces i dalje zavisi od pojedinaca.',
  'Osnova postoji; potrebno je povezati odgovornost i ritam praćenja.',
  'Proces je blizu stabilnog i preglednog načina rada.',
  'Dobar temelj — fokus može preći na optimizaciju i podršku.'
];

const easeOutQuint = [0.22, 1, 0.36, 1] as const;

export function WhyChooseUsSection() {
  const [selectedChecks, setSelectedChecks] = useState<boolean[]>(() =>
    processChecks.map(() => false)
  );
  const reduceMotion = useReducedMotion();
  const score = selectedChecks.filter(Boolean).length;

  const toggleCheck = (index: number) => {
    setSelectedChecks(current =>
      current.map((selected, itemIndex) =>
        itemIndex === index ? !selected : selected
      )
    );
  };

  return (
    <section className="py-22 sm:py-28 lg:py-38">
      <div className="site-container">
        <div className="grid gap-6 lg:grid-cols-[0.8fr_0.52fr] lg:items-end lg:justify-between lg:gap-24">
          <MotionReveal>
            <p className="section-label">Način saradnje</p>
            <h2 className="mt-3 max-w-3xl text-balance text-4xl font-semibold leading-[1.08] tracking-[-0.05em] text-text-primary sm:text-5xl lg:text-[3.5rem]">
              Od rasutog procesa do jasnog toka rada.
            </h2>
          </MotionReveal>
          <p className="max-w-lg text-base leading-7 text-text-secondary lg:pb-1">
            Pomerite granicu na fotografiji, zatim proverite koliko je vaš
            trenutni proces zaista definisan.
          </p>
        </div>

        <div className="mt-12 grid gap-10 lg:mt-16 lg:grid-cols-[1.12fr_0.88fr] lg:items-center lg:gap-18">
          <MotionReveal>
            <BeforeAfterSlider
              beforeSrc="https://images.unsplash.com/photo-1497215728101-856f4ea42174?auto=format&fit=crop&w=1500&q=86"
              beforeAlt="Radni prostor pre uređenja toka rada"
              afterSrc="https://images.unsplash.com/photo-1497366811353-6870744d04b2?auto=format&fit=crop&w=1500&q=86"
              afterAlt="Organizovan radni prostor nakon uređenja toka rada"
            />
          </MotionReveal>

          <MotionReveal delay={0.05}>
            <div className="border-y border-border py-6 sm:py-7">
              <div className="flex items-end justify-between gap-5">
                <div>
                  <p className="section-label">Brza procena procesa</p>
                  <p className="mt-2 font-semibold text-text-primary">
                    Označite šta vaš tim već ima.
                  </p>
                </div>
                <span className="text-sm font-semibold tabular-nums text-primary">
                  {score}/{processChecks.length}
                </span>
              </div>

              <div className="mt-6 border-t border-border">
                {processChecks.map((item, index) => {
                  const selected = selectedChecks[index];
                  return (
                    <button
                      key={item}
                      type="button"
                      className="group flex w-full cursor-pointer items-center gap-4 border-b border-border py-4 text-left"
                      onClick={() => toggleCheck(index)}
                      aria-pressed={selected}
                    >
                      <span
                        className={`grid size-6 shrink-0 place-items-center border transition-colors ${selected ? 'border-primary bg-primary text-on-primary' : 'border-border-strong text-transparent group-hover:border-primary'}`}
                        aria-hidden="true"
                      >
                        <Check className="size-3.5" />
                      </span>
                      <span
                        className={`text-sm font-medium transition-colors sm:text-base ${selected ? 'text-text-primary' : 'text-text-secondary group-hover:text-text-primary'}`}
                      >
                        {item}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="mt-6">
                <div
                  className="h-1 overflow-hidden bg-border"
                  aria-hidden="true"
                >
                  <motion.span
                    className="block h-full origin-left bg-primary"
                    initial={false}
                    animate={{ scaleX: score / processChecks.length }}
                    transition={{
                      duration: reduceMotion ? 0 : 0.28,
                      ease: easeOutQuint
                    }}
                  />
                </div>
                <div
                  className="mt-4 flex min-h-12 items-start justify-between gap-5"
                  aria-live="polite"
                >
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.p
                      key={score}
                      className="max-w-md text-sm leading-6 text-text-secondary"
                      initial={reduceMotion ? false : { opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={reduceMotion ? undefined : { opacity: 0, y: -4 }}
                      transition={{ duration: 0.2, ease: easeOutQuint }}
                    >
                      {assessmentMessages[score]}
                    </motion.p>
                  </AnimatePresence>
                  {score > 0 && (
                    <button
                      type="button"
                      className="animated-link shrink-0 cursor-pointer text-xs font-semibold text-text-muted transition-colors hover:text-primary"
                      onClick={() =>
                        setSelectedChecks(processChecks.map(() => false))
                      }
                    >
                      Poništi
                    </button>
                  )}
                </div>
              </div>
            </div>
          </MotionReveal>
        </div>
      </div>
    </section>
  );
}
